"use client";

import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InquirySortKey } from "cmw-client";
import type { DataTableQueryParams } from "@/components/data-table";
import { browserCmw } from "@/lib/ky/browser";

export const INQUIRIES_QUERY_KEY = ["inquiries"] as const;
export const INQUIRIES_UNREAD_COUNT_KEY = ["inquiries", "unread-count"] as const;

/** Columns `GET /inquiries` will sort on. Anything else is rejected by the API. */
const SORTABLE: InquirySortKey[] = ["createdAt", "fullName", "status"];

const toSortKey = (sortBy: string | undefined): InquirySortKey | undefined =>
  SORTABLE.find((key) => key === sortBy);

/**
 * `GET /inquiries` shaped for `DataTable` — spread the result straight onto it, same pattern as
 * `useInvitations`. Scoped to the caller server-side; nothing here needs a practitioner id.
 */
export const useInquiriesTable = () => {
  const queryFn = useCallback(
    ({ offset, limit, search, sortBy, sortOrder }: DataTableQueryParams) =>
      browserCmw.inquiries.list({
        offset,
        limit,
        search,
        /* Dropped rather than forwarded blindly: an unknown `sortBy` would 400 the whole page. */
        sortBy: toSortKey(sortBy),
        sortOrder,
      }),
    [],
  );

  return { queryKey: INQUIRIES_QUERY_KEY, queryFn };
};

type UseUnreadInquiriesCountOptions = {
  enabled?: boolean;
};

/** Backs the "N New" badges on the sidebar and the inbox heading. */
export const useUnreadInquiriesCount = ({ enabled = true }: UseUnreadInquiriesCountOptions = {}) =>
  useQuery({
    queryKey: INQUIRIES_UNREAD_COUNT_KEY,
    queryFn: () => browserCmw.inquiries.unreadCount(),
    enabled,
  });

/**
 * Flips an inquiry from "new" to "read" (`PATCH /inquiries/:id`). Fire-and-forget: the
 * practitioner is already looking at the message either way, so a failed status flip isn't worth
 * surfacing to them.
 */
export const useMarkInquiryRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => browserCmw.inquiries.updateStatus(id, { status: "read" }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: INQUIRIES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: INQUIRIES_UNREAD_COUNT_KEY }),
      ]);
    },
  });
};
